import { Injectable, computed, signal } from '@angular/core';

interface ShellBridge {
  channels?: readonly string[];
  invoke(channel: string, payload?: unknown): Promise<unknown>;
  on(channel: string, cb: (payload: unknown) => void): () => void;
}

/**
 * The desktop app's own updates (the Electron shell, not this web app).
 *
 * The shell downloads a new version in the background; once it is ready it pushes
 * `app.updateReady`, and `app.updateStatus` answers for a page that loads afterwards
 * (desktop src/main/updater.ts §2). This service turns that into "Crewdle X is ready
 * to install · Restart now · ×" (DesktopUpdatePillComponent): Restart invokes
 * `app.installUpdate`; × hides it for this session — the update stays in the tray
 * ("Restart to update") and installs on the next real quit.
 *
 * Inert on the web, under Capacitor, and in desktop builds older than 1.0.10 (their
 * `crewdleNative.channels` do not list the update channels).
 */
@Injectable({ providedIn: 'root' })
export class DesktopUpdateService {
  /** The version ready to install, or null. */
  public readonly readyVersion = signal<string | null>(null);
  private readonly dismissed = signal(false);
  public readonly visible = computed(() => !!this.readyVersion() && !this.dismissed());
  public readonly restarting = signal(false);

  private started = false;

  private get shell(): ShellBridge | null {
    const native = (window as unknown as { crewdleNative?: ShellBridge }).crewdleNative;
    return native?.channels?.includes('app.updateStatus') ? native : null;
  }

  public start(): void {
    const shell = this.shell;
    if (this.started || !shell) return;
    this.started = true;
    shell.on('app.updateReady', (payload) => {
      const version = (payload as { version?: string } | null)?.version;
      if (!version) return;
      this.readyVersion.set(version);
      // A newer download than the one dismissed: show it again.
      this.dismissed.set(false);
    });
    void shell
      .invoke('app.updateStatus')
      .then((result) => {
        const version = (result as { stagedVersion?: string | null } | null)?.stagedVersion;
        if (version) this.readyVersion.set(version);
      })
      .catch(() => undefined);
  }

  public restart(): void {
    const shell = this.shell;
    if (!shell || this.restarting()) return;
    this.restarting.set(true);
    shell.invoke('app.installUpdate').catch(() => this.restarting.set(false));
  }

  public dismiss(): void {
    this.dismissed.set(true);
  }
}
